import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import axios from "axios";
import { databaseURL, userRole, deconnecterUtilisateur } from "../../helpers.js";
import { deleteUser } from "../../store/userStore.js";
import Icon from "../icons/Icon.jsx";
import IllustrationInstallation from "./IllustrationsInstallation.jsx";
import {
  estIOS,
  estMobile,
  estInstalleeSurEcranAccueil,
  obtenirAbonnementActuel,
  activerNotificationsPush,
} from "../notifications/pushSubscription.js";

/**
 * Installation obligatoire de l'app avant usage (28/09/2026) — sur demande du client, après la
 * recette du push : Distributeur et Home Stock ne doivent plus pouvoir utiliser l'application
 * tant qu'elle n'est pas installée (mobile seulement, voir estMobile()) ET les notifications
 * activées. Aucune sortie de secours (décision du client) — seule la déconnexion reste possible.
 *
 * Se pose en overlay plein écran au-dessus de tout le reste de App.js, jamais pour
 * l'administrateur ni pour un compte non connecté. `PUT /users/onboarding/installed` est
 * rejouée à chaque détection positive ; la route est idempotente côté serveur, l'appel ne coûte
 * qu'une requête réseau de plus.
 */
export default function OnboardingObligatoire() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user?.value)?.payload;
  const [pret, setPret] = useState(false);
  const [installee, setInstallee] = useState(true);
  const [installeeReellement, setInstalleeReellement] = useState(false);
  const [abonne, setAbonne] = useState(true);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState("");
  const [etape, setEtape] = useState(1);
  const installationSignalee = useRef(false);

  const concerne =
    !!user?.token && (user.role === userRole.distributor || user.role === userRole.developper);
  const mobile = estMobile();

  // `ok` lève le blocage (desktop passe toujours) ; `reelle` ne vaut vrai que pour une
  // installation authentique — seule celle-ci doit être signalée au serveur, sans quoi tous les
  // comptes desktop remonteraient comme « installés » dans le suivi admin, sans l'être.
  const rafraichirEtat = React.useCallback(async () => {
    const reelle = estInstalleeSurEcranAccueil();
    setInstallee(!mobile || reelle);
    setInstalleeReellement(reelle);
    const abonnement = await obtenirAbonnementActuel().catch(() => null);
    setAbonne(!!abonnement);
    setPret(true);
  }, [mobile]);

  useEffect(() => {
    if (!concerne) {
      setPret(true);
      return undefined;
    }
    let annule = false;
    rafraichirEtat();

    // Réactif là où le navigateur le permet (Android/desktop) : si l'app est installée pendant
    // que cet onglet reste ouvert, le blocage se lève sans action de l'utilisateur. iOS n'émet
    // aucun événement équivalent (navigator.standalone est figé) — le bouton « Vérifier à
    // nouveau » reste le seul recours là-bas.
    const media = window.matchMedia?.("(display-mode: standalone)");
    const surChangement = () => {
      if (!annule) rafraichirEtat();
    };
    media?.addEventListener?.("change", surChangement);
    return () => {
      annule = true;
      media?.removeEventListener?.("change", surChangement);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [concerne, user?._id]);

  useEffect(() => {
    if (!concerne || !installeeReellement || installationSignalee.current || !user?.token) return;
    installationSignalee.current = true;
    axios
      .put(databaseURL + "users/onboarding/installed", {}, { headers: { Authorization: `Bearer ${user.token}` } })
      .catch(() => {
        // Silencieux : ce n'est qu'un signal pour le suivi admin, jamais une condition du
        // blocage lui-même (qui ne relit que l'état réel du terminal).
        installationSignalee.current = false;
      });
  }, [concerne, installeeReellement, user?.token]);

  const verifier = () => {
    rafraichirEtat();
  };

  // Ne contourne pas l'exigence — ramène simplement à l'écran de connexion, pour le compte
  // connecté par erreur qui n'a sinon aucun moyen de repartir depuis cet écran plein.
  const seDeconnecter = () => {
    deconnecterUtilisateur(user?.token);
    dispatch(deleteUser());
    localStorage.clear();
    sessionStorage.clear();
    navigate("/");
  };

  const activer = async () => {
    setEnCours(true);
    setErreur("");
    try {
      await activerNotificationsPush(user.token);
      setAbonne(true);
    } catch (err) {
      setErreur(err.message || t("onboardingEcran.erreurActivation"));
    } finally {
      setEnCours(false);
    }
  };

  if (!pret || !concerne || (installee && abonne)) return null;

  const plateforme = estIOS() ? "ios" : "android";
  const gras = { b: <b /> };

  const styleBoutonPrincipal = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    height: "48px",
    width: "100%",
    borderRadius: "12px",
    background: "var(--blue)",
    color: "#fff",
    fontSize: "15px",
    fontWeight: 700,
  };
  const styleLien = { height: "44px", fontSize: "13.5px", fontWeight: 700, background: "none" };

  // Installation (mobile, app pas encore installée) : un écran par étape, trois étapes — planches
  // OnboardingIOSEtape1..3 et OnboardingAndroidEtape1..3, validées le 29/09/2026.
  if (!installee) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 z-[9999] flex flex-col overflow-y-auto"
        style={{ background: "var(--bg)" }}
      >
        <div style={{ height: "4px", flexShrink: 0, background: "linear-gradient(90deg,#0180D0 0%,#F3901A 52%,#D2095F 100%)" }} />
        <div
          className="flex flex-col items-center text-center"
          style={{ flexGrow: 1, justifyContent: "space-between", padding: "26px 24px 20px", gap: "20px" }}
        >
          <div className="flex flex-col items-center" style={{ gap: "14px" }}>
            <span
              style={{
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: ".07em",
                textTransform: "uppercase",
                color: "var(--text-3)",
              }}
            >
              {t("onboardingEcran.etapeSur", { n: etape })}
            </span>
            <div className="flex" style={{ gap: "6px" }} aria-hidden="true">
              {[1, 2, 3].map((n) => (
                <span
                  key={n}
                  style={{
                    height: "8px",
                    width: n === etape ? "22px" : "8px",
                    borderRadius: "999px",
                    background: n === etape ? "var(--blue)" : "var(--border-2)",
                  }}
                />
              ))}
            </div>
          </div>

          <div className="flex flex-col items-center w-full" style={{ gap: "20px", maxWidth: "342px" }}>
            <div className="flex flex-col" style={{ gap: "8px", maxWidth: "320px" }}>
              <span
                className="font-['Bricolage_Grotesque',sans-serif]"
                style={{ fontSize: "19px", fontWeight: 700, lineHeight: 1.25, color: "var(--text)" }}
              >
                {t(etape === 3 ? "onboardingEcran.titreEtape3" : `onboardingEcran.${plateforme}Titre${etape}`)}
              </span>
              <span style={{ fontSize: "14px", lineHeight: 1.5, color: "var(--text-2)" }}>
                <Trans i18nKey={`onboardingEcran.${plateforme}Texte${etape}`} components={gras} />
              </span>
            </div>

            <IllustrationInstallation plateforme={plateforme} etape={etape} />

            {etape === 1 && (
              <span style={{ fontSize: "12px", lineHeight: 1.5, maxWidth: "300px", color: "var(--text-3)" }}>
                <Trans
                  i18nKey="onboardingEcran.autreNavigateur"
                  values={{ navigateur: plateforme === "ios" ? "Safari" : "Chrome" }}
                  components={gras}
                />
              </span>
            )}
          </div>

          <div className="flex flex-col w-full" style={{ gap: "4px", maxWidth: "342px" }}>
            {etape < 3 ? (
              <button type="button" onClick={() => setEtape(etape + 1)} style={styleBoutonPrincipal}>
                {t("onboardingEcran.suivant")}
              </button>
            ) : (
              <button type="button" onClick={verifier} style={styleBoutonPrincipal}>
                {t("onboardingEcran.boutonVerifier")}
              </button>
            )}
            {etape > 1 && (
              <button
                type="button"
                onClick={() => setEtape(etape - 1)}
                style={{ ...styleLien, color: "var(--text-2)" }}
              >
                {t("onboardingEcran.precedent")}
              </button>
            )}
            {etape === 3 && (
              <button type="button" onClick={seDeconnecter} style={{ ...styleLien, color: "var(--danger)" }}>
                {t("nav.seDeconnecter")}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Activation des notifications (app installée, ou ordinateur) — planche
  // OnboardingActivationNotifications.
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[9999] flex flex-col overflow-y-auto"
      style={{ background: "var(--bg)" }}
    >
      <div style={{ height: "4px", flexShrink: 0, background: "linear-gradient(90deg,#0180D0 0%,#F3901A 52%,#D2095F 100%)" }} />
      <div className="flex flex-col items-center justify-center text-center" style={{ flexGrow: 1, gap: "24px", padding: "24px" }}>
        <div
          className="flex items-center justify-center flex-shrink-0"
          style={{ width: "56px", height: "56px", borderRadius: "16px", background: "var(--blue-soft)" }}
        >
          <Icon name="bell" size={26} color="var(--blue-ink)" strokeWidth={1.8} />
        </div>

        <div className="flex flex-col" style={{ gap: "6px", maxWidth: "320px" }}>
          <span
            className="font-['Bricolage_Grotesque',sans-serif]"
            style={{ fontSize: "18px", fontWeight: 700, color: "var(--text)" }}
          >
            {t("onboardingEcran.titreActivation")}
          </span>
          <span style={{ fontSize: "13.5px", lineHeight: 1.5, color: "var(--text-2)" }}>
            {t("onboardingEcran.sousTitreActivation")}
          </span>
        </div>

        {erreur && <span style={{ fontSize: "12.5px", color: "var(--danger)" }}>{erreur}</span>}

        <div className="flex flex-col w-full" style={{ gap: "12px", maxWidth: "320px" }}>
          <button
            type="button"
            disabled={enCours}
            onClick={activer}
            style={{ ...styleBoutonPrincipal, opacity: enCours ? 0.6 : 1 }}
          >
            <Icon name="bell" size={18} color="currentColor" />
            {t("compteEcran.activerLesNotifications")}
          </button>
          <button type="button" onClick={seDeconnecter} style={{ ...styleLien, color: "var(--danger)" }}>
            {t("nav.seDeconnecter")}
          </button>
        </div>
      </div>
    </div>
  );
}
